import React from 'react';
import PropTypes, { any } from 'prop-types';

import { getLinkProps } from 'utils/get-link-props';
import { eventTrack, LINK_LIST_CTA_CLICK } from 'utils/google-tag-manager';

import { ThemeProvider } from 'styled-components';
import { Headline, Paragraph } from 'components/atoms';
import { ModuleRenderer } from 'utils/module-renderer';
import { useSplitview } from 'queries/splitview';
import { useRouter } from 'next/router';
import * as Styled from './SplitView.styled';

const onClickCta = event => eventTrack(LINK_LIST_CTA_CLICK, { event });

/**
 * Creates a list of links.
 * @param {string} id The split view module ID
 * @param {string} title
 * @param {string} eyebrow
 * @param {string} description
 * @param {object} cta
 * @param backgroundColor
 * @param fullWidth
 * @returns {ReactElement}
 */
const SplitView = ({
  id,
  title = '',
  eyebrow = '',
  description = '',
  cta = null,
  backgroundColor = '',
  fullWidth = false,
}) => {
  const router = useRouter();
  const { locale } = router;
  const { leftContent, rightContent, loading } = useSplitview(id, locale);
  if (loading || !leftContent || !rightContent) {
    return null;
  }

  const hasCta = !!cta;
  const hasText = !!title;

  const renderModule = module => {
    if (!module) return null;

    return <ModuleRenderer components={[module]} />;
  };

  const renderHeader = () => {
    return (
      <Styled.HeaderContainer>
        <Styled.TitleContainer>
          {eyebrow && <Styled.Eyebrow>{eyebrow}</Styled.Eyebrow>}
          <Headline tag="h1" styling="h1" text={title} />
        </Styled.TitleContainer>
        <Styled.DescriptionContainer>
          <Paragraph>{description}</Paragraph>
          {cta && (
            <Styled.Cta
              type="primary"
              isDarkTheme
              overrideFunctionality={cta.overrideFunctionality}
              {...getLinkProps(cta.url)}
              onClick={onClickCta}
            >
              {cta.title}
            </Styled.Cta>
          )}
        </Styled.DescriptionContainer>
      </Styled.HeaderContainer>
    );
  };

  return (
    <ThemeProvider
      theme={{
        hasCta,
        backgroundColor,
        hasText,
        fullWidth,
        hasLeftContent: !!leftContent,
        hasRightContent: !!rightContent,
      }}
    >
      <Styled.Container>
        {!hasCta && hasText && leftContent && renderHeader()}
        <Styled.ModuleContainer>
          <Styled.LeftContent>
            {((hasCta && hasText) || !leftContent) && renderHeader()}
            {leftContent && renderModule(leftContent)}
          </Styled.LeftContent>
          <Styled.RightContent>
            {rightContent && renderModule(rightContent)}
          </Styled.RightContent>
        </Styled.ModuleContainer>
      </Styled.Container>
    </ThemeProvider>
  );
};

SplitView.propTypes = {
  id: PropTypes.string.isRequired,
  /**
   * The title of the component
   */
  title: PropTypes.string,
  /**
   * The eyebrow of the component
   */
  eyebrow: PropTypes.string,
  /**
   * The description of the component
   */
  description: PropTypes.string,
  /**
   * The cta
   */
  cta: PropTypes.objectOf(any),
};

export default SplitView;
